import React from "react";

export const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [5, 10, 25, 50, "All"],
  className = "",
  labelledBy,
}) => {
  const startItem =
    itemsPerPage === "All" ? 1 : (currentPage - 1) * itemsPerPage + 1;
  const endItem =
    itemsPerPage === "All"
      ? totalItems
      : Math.min(currentPage * itemsPerPage, totalItems);
  const pageSelectId = labelledBy
    ? `${labelledBy}-per-page`
    : "results-per-page";

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      onPageChange(page);
    }
  };

  const controlClass =
    "flex items-center justify-center min-h-11 min-w-11 lg:min-h-0 lg:h-8 lg:w-8 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand-gold-ink focus:ring-offset-1";

  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}
    >
      <div className="flex items-center gap-2">
        <label htmlFor={pageSelectId} className="text-sm text-brand-muted">
          Show
        </label>
        <select
          id={pageSelectId}
          value={itemsPerPage}
          aria-label="Results per page"
          onChange={(e) =>
            onItemsPerPageChange(
              e.target.value === "All" ? "All" : Number(e.target.value)
            )
          }
          className="border border-brand-charcoal/20 rounded-lg px-3 py-1.5 max-lg:min-h-11 text-sm bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
        >
          {itemsPerPageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <span className="text-sm text-brand-muted">entries</span>
      </div>

      <div className="text-sm text-brand-muted" aria-live="polite">
        Showing {startItem} to {endItem} of {totalItems} results
      </div>

      {itemsPerPage !== "All" && (
        <div className="flex items-center gap-1" role="group" aria-label="Pagination">
          <button
            type="button"
            onClick={() => handlePageClick(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
            className={`${controlClass} text-brand-muted bg-white border border-brand-charcoal/20 hover:bg-brand-cream hover:text-brand-charcoal disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-brand-muted`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {getPageNumbers().map((page, index) =>
            page === "..." ? (
              <span
                key={`ellipsis-${index}`}
                className="flex items-center justify-center w-8 h-8 text-sm text-brand-mist"
                aria-hidden="true"
              >
                ...
              </span>
            ) : (
              <button
                type="button"
                key={page}
                onClick={() => handlePageClick(page)}
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? "page" : undefined}
                className={`${controlClass} ${
                  page === currentPage
                    ? "bg-brand-charcoal text-brand-cream border border-brand-charcoal"
                    : "text-brand-charcoal bg-white border border-brand-charcoal/20 hover:bg-brand-cream"
                }`}
              >
                {page}
              </button>
            )
          )}

          <button
            type="button"
            onClick={() => handlePageClick(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
            className={`${controlClass} text-brand-muted bg-white border border-brand-charcoal/20 hover:bg-brand-cream hover:text-brand-charcoal disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-brand-muted`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
